import React, { useState, useEffect } from 'react';
import './tabs.css';
import { FaDownload, FaEye } from "react-icons/fa";
import { TbReplace } from "react-icons/tb";

const OrderTabs = ({ orderId }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageAction, setImageAction] = useState('');
  const [currentEditingImage, setCurrentEditingImage] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch images from API based on orderId
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://backend.hira-tech.co.uk/order/order/image/${orderId}/`);
        if (!response.ok) {
          throw new Error('The status images for this order have not been added yet.');
        }
        const data = await response.json();
        setImages(data.images || []);
      } catch (err) {
        console.error('Error fetching images:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchImages();
    }
  }, [orderId]);

  // Group images by status
  const imageSections = {
    section1: images.map(img => ({
      id: img.id,
      url: img.image_url,
      title: img.status || 'Order Image',
      date: img.date_uploaded
    }))
  };

  // Fetch notes from backend
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const response = await fetch(`https://backend.hira-tech.co.uk/order/api/order/${orderId}/notes/`);
        if (!response.ok) {
          throw new Error('Failed to fetch notes');
        }
        const data = await response.json();

        const transformedNotes = data.map((item, index) => ({
          id: index + 1,
          text: item.note,
          date: new Date(item.created_at).toISOString().split('T')[0],
          author: 'Admin'
        }));

        setNotes(transformedNotes);
      } catch (err) {
        console.error('Failed to fetch notes:', err);
      }
    };

    if (orderId) {
      fetchNotes();
    }
  }, [orderId]);

  const handleAddNote = async () => {
    if (newNote.trim()) {
      const noteData = {
        note: newNote,
        is_required: false  // You can also add a checkbox in UI if needed
      };

      try {
        const response = await fetch(`https://backend.hira-tech.co.uk/order/api/order/${orderId}/notes/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(noteData)
        });

        if (!response.ok) {
          throw new Error('Failed to add note');
        }

        // Reload notes from backend after successful add
        const newNoteObj = {
          id: notes.length + 1,
          text: newNote,
          date: new Date().toISOString().split('T')[0],
          author: 'Current User'
        };

        setNotes([...notes, newNoteObj]);
        setNewNote('');
      } catch (err) {
        console.error('Error adding note:', err);
      }
    }
  };

  const handleImageAction = (action, image) => {
    setImageAction(action);
    setCurrentEditingImage(image);

    if (action === 'view') {
      setPreviewImage(image.url);
    } else {
      setPreviewImage(null);
      setSelectedFile(null);
    }

    setShowImageModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const confirmImageReplace = async () => {
    if (!selectedFile || !currentEditingImage) return;

    try {
      // In a real app, you would upload to your server here
      // This is just a mock implementation
      const updatedImages = images.map(img =>
        img.id === currentEditingImage.id
          ? {
              ...img,
              image_url: previewImage,
              status: 'Replaced Image',
              date_uploaded: new Date().toISOString()
            }
          : img
      );

      setImages(updatedImages);
      setShowImageModal(false);
      setSelectedFile(null);
      setPreviewImage(null);
    } catch (err) {
      console.error('Error replacing image:', err);
    }
  };

  const downloadImage = (imageUrl, imageTitle) => {
    fetch(imageUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${imageTitle || 'image'}-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(err => console.error('Error downloading image:', err));
  };

  return (
    <div className="lot-container">
      {/* Tabs Section */}
      <div className="lot-tabs">
        <button
          className={`lot-tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`lot-tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </button>
      </div>

      {/* Tab Content */}
      <div className="lot-tab-content">
        {activeTab === 'details' ? (
          <div className="details-content">
            <h2>Image Details</h2>

            {loading ? (
              <p>Loading images...</p>
            ) : error ? (
              <p className="error">Error: {error}</p>
            ) : (
              <div className="image-section">
                <h3>Order Images</h3>
                {images.length === 0 ? (
                  <p>No images found for this order.</p>
                ) : (
                  <div className="image-cards">
                    {imageSections.section1.map((image, index) => (
                      <div key={image.id} className="image-card">
                        <div className="image-container">
                          <img
                            src={image.url}
                            alt={image.title}
                            className="card-image"
                            onClick={() => handleImageAction('view', image)}
                          />
                        </div>
                        <div className="image-info">
                          <p>{image.title}</p>
                          <p>{new Date(image.date).toLocaleString()}</p>
                        </div>
                        <div className="image-actions">
                          <button
                            onClick={() => handleImageAction('replace', image)}
                            title="Replace Image"
                          >
                            <TbReplace/>
                          </button>
                          <button
                            onClick={() => downloadImage(image.url, image.title)}
                            title="Download Image"
                          >
                            <FaDownload/>
                          </button>
                          <button
                            onClick={() => handleImageAction('view', image)}
                            title="View Image"
                          >
                            <FaEye/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="notes-content">
            <h2>Order Notes</h2>
            <div className="notes-list">
              {notes.map(note => (
                <div key={note.id} className="note">
                  <div className="note-header">
                    <span className="note-author">{note.author}</span>
                    <span className="note-date">{note.date}</span>
                  </div>
                  <div className="note-text">{note.text}</div>
                </div>
              ))}
            </div>
            <div className="add-note">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a new note..."
              />
              <button onClick={handleAddNote}>Add Note</button>
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="image-modal">
          <div className="modal-content">
            <button
              className="close-modal"
              onClick={() => {
                setShowImageModal(false);
                setSelectedFile(null);
                setPreviewImage(null);
              }}
            >
              &times;
            </button>
            <h3>
              {imageAction === 'replace' ? 'Replace Image' : 'View Image'}
            </h3>

            {imageAction === 'replace' ? (
              <div className="replace-image-container">
                <div className="file-selection-form">
                  <label className="file-input-label">
                    Choose New Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </label>
                  {selectedFile && (
                    <div className="file-info">
                      <p>Selected file: {selectedFile.name}</p>
                      <p>Size: {(selectedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                  )}
                </div>

                <div className="image-comparison">
                  <div className="image-box">
                    <h4>Current Image</h4>
                    <img
                      src={currentEditingImage.url}
                      alt="Current"
                      className="comparison-image"
                    />
                  </div>
                  <div className="image-box">
                    <h4>New Image</h4>
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="comparison-image"
                      />
                    ) : (
                      <div className="no-image-placeholder">
                        No image selected
                      </div>
                    )}
                  </div>
                </div>

                <button
                  className="replace-button"
                  onClick={confirmImageReplace}
                  disabled={!selectedFile}
                >
                  Confirm Replacement
                </button>
              </div>
            ) : (
              <div className="view-image-container">
                <img
                  src={currentEditingImage.url}
                  alt={currentEditingImage.title}
                  className="modal-view-image"
                />
                <div className="image-meta">
                  <p><strong>Title:</strong> {currentEditingImage.title}</p>
                  <p><strong>Date:</strong> {new Date(currentEditingImage.date).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTabs;